import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Chip,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  Save,
  CheckCircle,
  Cancel,
  History,
  CloudOff,
  CloudUpload,
  Sync
} from '@mui/icons-material';
import dayjs from 'dayjs';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ROLES } from '../../constants/roles';
import {
  DEFAULT_TECHNICAL_LOG_BOOK_DEFINITION,
  getMaxSectorsPerTechlogBook,
  getTotalsSection,
  MAX_TECHNICAL_LOG_BOOK_SECTOR_SLOTS
} from '../../constants/technicalLogBookDefinition';
import { formatDateTimeWithTimezone } from '../../utils/timezoneUtils';
import { computeTotalCarriedForward, parseHMMToHours, roundTotalsNumeric } from '../../utils/technicalLogBookCarriedForward';
import {
  applyAmeSignatureAutofillToSector,
  isAmeSignatureTypePath,
  isAmeSignatureValuePath
} from '../../utils/technicalLogBookAmeSignature';
import {
  getOfflineDraft,
  isNetworkError,
  mergeOfflineDraftIntoEntry,
  removeOfflineDraft,
  saveOfflineDraft
} from '../../utils/offlineTechlogStore';
import { useOfflineTechlogSync } from '../../hooks/useOfflineTechlogSync';
import { renderSectorForm } from './TechnicalLogBookEntry-part2';
import FieldHistorySection from './TechnicalLogBookEntryComponents/FieldHistorySection';
import HeaderTab from './TechnicalLogBookEntryComponents/HeaderTab';
import TotalsTab from './TechnicalLogBookEntryComponents/TotalsTab';
import AdditionalFieldsTab from './TechnicalLogBookEntryComponents/AdditionalFieldsTab';
import ActionDialogs from './TechnicalLogBookEntryComponents/ActionDialogs';

const parseFourDigitMinutes = (value) => {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  if (!/^\d{3,4}$/.test(text)) return null;

  const num = Number(text);
  if (!Number.isFinite(num)) return null;

  const minutesPart = num % 100; // last two digits
  const hoursPart = Math.floor(num / 100);

  // Basic validation: minutes must be < 60; hours non-negative
  if (minutesPart < 0 || minutesPart >= 60 || hoursPart < 0) return null;

  return hoursPart * 60 + minutesPart;
};

const formatMinutesToHHMM = (minutes) => {
  if (!Number.isFinite(minutes) || minutes < 0) return '';
  const hoursPart = Math.floor(minutes / 60);
  const minutesPart = minutes % 60;
  return `${String(hoursPart).padStart(2, '0')}${String(minutesPart).padStart(2, '0')}`;
};

const calculateBlockTimeHHMM = (rotorStartTimeUTC, rotorStopTimeUTC) => {
  const startMinutes = parseFourDigitMinutes(rotorStartTimeUTC);
  const stopMinutes = parseFourDigitMinutes(rotorStopTimeUTC);
  if (startMinutes === null || stopMinutes === null) return '';

  const diffMinutes = stopMinutes - startMinutes;
  if (diffMinutes < 0) return '';
  return formatMinutesToHHMM(diffMinutes);
};

const parseSheetNumber = (value) => {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  if (!/^\d+$/.test(text)) return null;
  const num = Number(text);
  return Number.isInteger(num) ? num : null;
};

const isAutoFillEligibleTotalBroughtForward = (totalBroughtForward) => {
  if (!totalBroughtForward || typeof totalBroughtForward !== 'object') return true;
  const values = Object.values(totalBroughtForward);
  if (values.length === 0) return true;
  return values.every((v) => {
    if (v === null || v === undefined || v === '') return true;
    const n = Number(v);
    return Number.isFinite(n) && n === 0;
  });
};

const getLastSectorArrivalPlace = (entryData) => {
  if (!entryData || typeof entryData !== 'object') return '';
  for (let i = 5; i >= 1; i -= 1) {
    const arrivalPlace = entryData?.[`sector${i}`]?.arrivalPlace;
    if (typeof arrivalPlace === 'string' && arrivalPlace.trim()) return arrivalPlace.trim();
  }
  return '';
};

/** Fuel after flight from the chronologically last sector present on the entry (sector5 → sector1). */
const getLastSectorFuelAfterFlight = (entryData) => {
  if (!entryData || typeof entryData !== 'object') return null;
  for (let i = 5; i >= 1; i -= 1) {
    const sector = entryData[`sector${i}`];
    if (!sector || typeof sector !== 'object') continue;
    const n = Number(sector.fuelAfterFlight ?? 0);
    return Number.isFinite(n) ? n : 0;
  }
  return null;
};

const OMITTED_BROUGHT_FORWARD_KEYS = new Set([
  'flightAirTime',
  'totalAirTime',
  'blockTime',
  'totalLandings'
]);

const sanitizeBroughtForwardTotals = (totals) => {
  if (!totals || typeof totals !== 'object') return {};
  return Object.entries(totals).reduce((acc, [key, value]) => {
    if (OMITTED_BROUGHT_FORWARD_KEYS.has(key)) return acc;
    acc[key] = value;
    return acc;
  }, {});
};

const getHighestExistingSectorNumber = (entryData) => {
  if (!entryData || typeof entryData !== 'object') return 0;
  for (let i = MAX_TECHNICAL_LOG_BOOK_SECTOR_SLOTS; i >= 1; i -= 1) {
    if (entryData[`sector${i}`] && typeof entryData[`sector${i}`] === 'object') return i;
  }
  return 0;
};

const isAutoFillEligibleSector1FuelInTankBF = (sector1) => {
  if (!sector1 || typeof sector1 !== 'object') return false;
  const bf = Number(sector1.fuelInTankBF ?? 0);
  const uplift = Number(sector1.fuelUplifted ?? 0);
  const after = Number(sector1.fuelAfterFlight ?? 0);
  if (!Number.isFinite(bf)) return true;
  if (bf !== 0) return false;
  return (!Number.isFinite(uplift) || uplift === 0) && (!Number.isFinite(after) || after === 0);
};
const TechnicalLogBookEntry = ({
  open,
  onClose,
  entry,
  onApprove,
  onReject,
  onStartReview,
  onOpenEntry,
  onOfflineDraftChange,
  canApprove,
  canEdit,
  displayTimezone = 'Asia/Kolkata'
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(null);
  const [fieldHistory, setFieldHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [startReviewDialogOpen, setStartReviewDialogOpen] = useState(false);
  const [correctionDialogOpen, setCorrectionDialogOpen] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [unarchiveDialogOpen, setUnarchiveDialogOpen] = useState(false);
  const [unarchiveReason, setUnarchiveReason] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteDependencies, setDeleteDependencies] = useState([]);
  const [deletePreviewLoading, setDeletePreviewLoading] = useState(false);
  const [natureOfFlightMapping, setNatureOfFlightMapping] = useState({});
  const [unitPreferences, setUnitPreferences] = useState({
    FUEL: 'kg',
    ALTITUDE: 'ft',
    TEMPERATURE: '°C',
    PRESSURE: 'PSI',
    SPEED: 'knots'
  });
  const [availableSectors, setAvailableSectors] = useState([]); // Sectors from the trip/charter
  const [allCrewOptions, setAllCrewOptions] = useState([]);
  const [formatDefinition, setFormatDefinition] = useState(null); // Pinned entry format, else aircraft/default
  const [hasOfflinePending, setHasOfflinePending] = useState(false);
  const { user } = useAuth();
  const { showToast } = useToast();

  const handleOfflineSynced = useCallback(
    (entryId, serverEntry) => {
      if (!entryId || !entry || String(entry._id) !== String(entryId)) return;
      if (serverEntry) {
        setFormData((prev) => ({
          ...(prev || {}),
          ...serverEntry,
          _offlinePending: false,
          _offlineUpdatedAt: null
        }));
      } else {
        setFormData((prev) => (prev ? { ...prev, _offlinePending: false, _offlineUpdatedAt: null } : prev));
      }
      setHasOfflinePending(false);
      if (typeof onOfflineDraftChange === 'function') onOfflineDraftChange();
    },
    [entry, onOfflineDraftChange]
  );

  const {
    isOnline,
    syncing: offlineSyncing,
    syncOne,
    refreshPending
  } = useOfflineTechlogSync({
    enabled: open,
    onSynced: handleOfflineSynced
  });

  useEffect(() => {
    if (!open || !entry) return;
    let cancelled = false;

    const load = async () => {
      setFormatDefinition(null);
      try {
        const draft = await getOfflineDraft(entry._id);
        if (cancelled) return;
        if (draft?.payload) {
          setFormData(mergeOfflineDraftIntoEntry(entry, draft));
          setHasOfflinePending(true);
        } else {
          setFormData(entry);
          setHasOfflinePending(Boolean(entry._offlinePending));
        }
      } catch (err) {
        console.error('Error loading offline techlog draft:', err);
        if (!cancelled) {
          setFormData(entry);
          setHasOfflinePending(Boolean(entry._offlinePending));
        }
      }
      if (!cancelled) {
        fetchFieldHistory(entry._id);
        fetchSectorsFromTrip();
        fetchCrewOptions();
        resolveFormatDefinition();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, entry?._id]);

  useEffect(() => {
    fetchNatureOfFlightMapping();
  }, []);

  useEffect(() => {
    const autoFillTotalBroughtForward = async () => {
      if (!entry || !formData) return;
      const currentSheetNumber = parseSheetNumber(formData.sheetNumber);
      if (!currentSheetNumber || currentSheetNumber <= 1) return;
      if (!isAutoFillEligibleTotalBroughtForward(formData.totalBroughtForward)) return;

      const aircraftId = formData.aircraftId?._id || formData.aircraftId;
      if (!aircraftId) return;

      try {
        const source = await getBroughtForwardSourceEntry();
        if (!source) return;
        updateField('totalBroughtForward', sanitizeBroughtForwardTotals(source.sourceEntry.totalCarriedForward));
        showToast(
          `Auto-filled Total Brought Forward from sheet ${source.sourceSheetNumber}.`,
          'info'
        );
      } catch (error) {
        console.error('Error auto-filling Total Brought Forward:', error);
      }
    };

    autoFillTotalBroughtForward();
  }, [entry, formData?._id, formData?.sheetNumber]);
  useEffect(() => {
    const autoFillFirstSectorDeparturePlace = async () => {
      if (!entry || !formData) return;
      const sector1 = formData.sector1;
      if (!sector1 || typeof sector1 !== 'object') return;
      if (typeof sector1.departurePlace === 'string' && sector1.departurePlace.trim()) return;
      const previous = await getPreviousSheetEntry();
      if (!previous?.previousEntry) return;
      const previousLastArrivalPlace = getLastSectorArrivalPlace(previous.previousEntry);
      if (!previousLastArrivalPlace) return;
      updateField('sector1.departurePlace', previousLastArrivalPlace);
    };
    autoFillFirstSectorDeparturePlace().catch((error) => {
      console.error('Error auto-filling Sector 1 departure place:', error);
    });
    }, [entry, formData?._id, formData?.sheetNumber, formData?.sector1?.departurePlace]);


  const resolveFormatDefinition = async () => {
    if (!entry) return;
    const pinnedFormatId = entry.technicalLogBookFormatId?._id || entry.technicalLogBookFormatId;
    if (pinnedFormatId) {
      try {
        const formatRes = await axios.get(`/api/technical-log-book-format/${pinnedFormatId}`);
        const format = formatRes.data;
        const definition = format?.definition && typeof format.definition === 'object'
          ? format.definition
          : DEFAULT_TECHNICAL_LOG_BOOK_DEFINITION;
        setFormatDefinition(definition);
        return;
      } catch (err) {
        if (err.response?.status === 404) {
          console.warn('Pinned technical log book format not found; using default definition');
        } else {
          console.error('Error resolving pinned format definition:', err);
        }
        setFormatDefinition(DEFAULT_TECHNICAL_LOG_BOOK_DEFINITION);
        return;
      }
    }

    const aircraftId = entry.aircraftId?._id || entry.aircraftId;
    if (!aircraftId) {
      setFormatDefinition(DEFAULT_TECHNICAL_LOG_BOOK_DEFINITION);
      return;
    }
    try {
      const aircraftRes = await axios.get(`/api/aircraft/${aircraftId}`);
      const aircraft = aircraftRes.data;
      const formatId = aircraft?.approvedTechnicalLogBookFormatId?._id || aircraft?.approvedTechnicalLogBookFormatId;
      const formatRes = formatId
        ? await axios.get(`/api/technical-log-book-format/${formatId}`)
        : await axios.get('/api/technical-log-book-format/default');
      const format = formatRes.data;
      const definition = format?.definition && typeof format.definition === 'object'
        ? format.definition
        : DEFAULT_TECHNICAL_LOG_BOOK_DEFINITION;
      setFormatDefinition(definition);
    } catch (err) {
      console.error('Error resolving format definition:', err);
      setFormatDefinition(DEFAULT_TECHNICAL_LOG_BOOK_DEFINITION);
    }
  };

  const fetchNatureOfFlightMapping = async () => {
    try {
      const response = await axios.get('/api/client/settings');
      const mapping = response.data.measurementUnits?.natureOfFlightMapping || {};
      setNatureOfFlightMapping(mapping);
      const preference = response.data.measurementUnits?.preference || 'PER_AIRCRAFT';
      if (preference === 'SYSTEM_WIDE' && response.data.measurementUnits?.systemWide) {
        setUnitPreferences({
          FUEL: response.data.measurementUnits.systemWide.FUEL || 'kg',
          ALTITUDE: response.data.measurementUnits.systemWide.ALTITUDE || 'ft',
          TEMPERATURE: response.data.measurementUnits.systemWide.TEMPERATURE || '°C',
          PRESSURE: response.data.measurementUnits.systemWide.PRESSURE || 'PSI',
          SPEED: response.data.measurementUnits.systemWide.SPEED || 'knots'
        });
      }
    } catch (error) {
      console.error('Error fetching Nature of Flight mapping:', error);
    }
  };

  const handleAddNatureOfFlightMapping = async (code, description) => {
    const response = await axios.post('/api/client/settings/nature-of-flight', { code, description });
    const mapping = response.data?.natureOfFlightMapping || {};
    setNatureOfFlightMapping(mapping);
    showToast(`Nature of Flight code "${code}" saved`, 'success');
    return mapping;
  };

  const fetchFieldHistory = async (entryId) => {
    try {
      const response = await axios.get(`/api/technical-log-book/${entryId}/history`);
      setFieldHistory(response.data);
    } catch (error) {
      console.error('Error fetching field history:', error);
    }
  };

  const fetchCrewOptions = async () => {
    try {
      const response = await axios.get('/api/technical-log-book/crew-options');
      setAllCrewOptions(response.data || []);
    } catch (error) {
      console.error('Error fetching TLB crew options:', error);
      setAllCrewOptions([]);
    }
  };

  const fetchSectorsFromTrip = async () => {
    if (!entry?.sectorId) return;
    try {
      // The entry already has sectorId populated, check if it has tripId
      // If not, fetch the sector to get tripId
      let tripId = null;
      if (entry.sectorId?.tripId) {
        tripId = entry.sectorId.tripId;
      } else if (entry.sectorId?._id) {
        // Fetch sector to get tripId
        try {
          const sectorResponse = await axios.get(`/api/ops/sectors/${entry.sectorId._id || entry.sectorId}`);
          if (sectorResponse.data?.tripId) {
            tripId = sectorResponse.data.tripId;
          }
        } catch (sectorError) {
          console.error('Error fetching sector:', sectorError);
        }
      }
      
      if (tripId) {
        // Get all sectors for this trip
        const tripResponse = await axios.get(`/api/sales/trips/${tripId}`);
        const sectors = tripResponse.data.sectors || [];
        setAvailableSectors(sectors.sort((a, b) => (a.seqNo || 0) - (b.seqNo || 0)));
      }
    } catch (error) {
      console.error('Error fetching sectors from trip:', error);
    }
  };

  // Get list of active sectors (sector1, sector2, etc. that exist in formData)
  const getActiveSectors = () => {
    const active = [];
    for (let i = 1; i <= MAX_TECHNICAL_LOG_BOOK_SECTOR_SLOTS; i++) {
      if (formData?.[`sector${i}`]) {
        active.push(i);
      }
    }
    return active;
  };

  // Add a new sector
  const buildSectorPayload = (sectorNumber, sourceData = formData) => {
    if (!sourceData || sectorNumber < 2 || sectorNumber > MAX_TECHNICAL_LOG_BOOK_SECTOR_SLOTS) return null;
    const availableSector = availableSectors.find(s => s.seqNo === sectorNumber);
    const previousSectorArrivalPlace = sourceData?.[`sector${sectorNumber - 1}`]?.arrivalPlace;
    const derivedDeparturePlace = (typeof previousSectorArrivalPlace === 'string' && previousSectorArrivalPlace.trim())
      ? previousSectorArrivalPlace.trim()
      : (availableSector?.departure?.airport || '');
    const prevSector = sourceData?.[`sector${sectorNumber - 1}`];
    const prevFuelAfter = Number(prevSector?.fuelAfterFlight ?? 0);
    const derivedFuelInTankBF = Number.isFinite(prevFuelAfter) ? prevFuelAfter : 0;

    return {
      sectorNumber: sectorNumber,
      picName: '',
      coPilotName: '',
      natureOfFlight: 'N',
      departurePlace: derivedDeparturePlace,
      departureDate: availableSector?.departure?.date ? new Date(availableSector.departure.date) : new Date(),
      arrivalPlace: availableSector?.arrival?.airport || '',
      arrivalDate: availableSector?.arrival?.estimatedDate ? new Date(availableSector.arrival.estimatedDate) : new Date(),
      rotorStartTimeUTC: '',
      rotorStopTimeUTC: '',
      blockTime: '',
      airboneTime: '',
      landings: 0,
      iapCount: 0,
      fuelInTankBF: derivedFuelInTankBF,
      fuelUplifted: 0,
      totalFuelBeforeFlight: derivedFuelInTankBF,
      fuelAfterFlight: 0,
      fuelConsumption: 0,
      lhEngineOilInTankBF: 0,
      lhEngineOilUplifted: 0,
      lhEngineOilBeforeFlight: 0,
      lhEngineOilAfterFlight: 0,
      lhEngineOilConsumption: 0,
      rhEngineOilInTankBF: 0,
      rhEngineOilUplifted: 0,
      rhEngineOilBeforeFlight: 0,
      rhEngineOilAfterFlight: 0,
      rhEngineOilConsumption: 0,
      cruiseParameters: {
        altitudeZp: 0,
        oat: 0,
        ias: 0,
        nr: 0,
        lhEngineTRQ: 0,
        lhEngineN1Ng: 0,
        lhEngineITTTOT: 0,
        lhEngineOilTemperature: 0,
        lhEngineOilPressure: 0,
        rhEngineTRQ: 0,
        rhEngineN1Ng: 0,
        rhEngineITTTOT: 0,
        rhEngineOilTemperature: 0,
        rhEngineOilPressure: 0,
        mgbOilTemperature: 0,
        mgbOilPressure: 0,
        tgbOilTemp: 0,
        exedenceIfAny: '',
        misc: '',
        pilotSignature: '',
        pilotSignatureType: 'NAME'
      },
      defectReport: {
        pilotDefectReport: 'NIL',
        pilotCHPLNumber: '',
        pilotDefectTime: '',
        ameDefectReport: 'NIL',
        ameAuthNumber: '',
        ameDefectTime: ''
      },
      inspections: [],
      melReleased: 'No',
      bffCertification: {
        bffText: 'Certified that the BFF inspection has been carried out i.a.w approved',
        bffTaskCardIssued: '',
        bffTaskCardReviewed: '',
        bffEquipmentText: 'All operational and Emergency Equipment are correctly installed and found Serviceable',
        ameAuthNumber: '',
        bffDate: null,
        bffTime: ''
      },
      pilotAcceptance: {
        acceptanceText: 'Certified that the conditions stipulated in CAR Section 8 Series \'O\' Part 4, Para 2.3.1 as well as Information in Sec 4 & 5 of this Tech log book has been read and understood.',
        chplNumber: '',
        acceptanceTime: '',
        acceptanceDate: null
      },
      engineCleaningDetailsBFF: {
        externalWash: 'No',
        compressorWash: 'No'
      }
    };
  };

  const createAndOpenOverflowTechlogBook = async (overflowSectorNumber) => {
    const aircraftId = formData?.aircraftId?._id || formData?.aircraftId;
    if (!aircraftId) {
      showToast('Unable to create next techlogbook: aircraft missing', 'error');
      return;
    }

    const currentSheetNumber = parseSheetNumber(formData.sheetNumber);
    const nextSheetNumber = Number.isInteger(currentSheetNumber) ? String(currentSheetNumber + 1) : '';
    const headerPayload = {
      operatorName: formData.operatorName || '',
      aircraftType: formData.aircraftType || '',
      aircraftRegistration: formData.aircraftRegistration || '',
      aircraftSerialNumber: formData.aircraftSerialNumber || '',
      entryDate: formData.entryDate || new Date(),
      maintenanceDetails: formData.maintenanceDetails || '',
      workOrderNumber: formData.workOrderNumber || '',
      workOrderDate: formData.workOrderDate || null,
      baTestTime: formData.baTestTime || null,
      crsNumber: formData.crsNumber || '',
      crsDate: formData.crsDate || null,
      crsValue: formData.crsValue || {},
      amoName: formData.amoName || '',
      componentDue: formData.componentDue || {},
      nextScheduledMaintenance: formData.nextScheduledMaintenance || {},
      sheetNumber: nextSheetNumber,
      totalBroughtForward: sanitizeBroughtForwardTotals(formData.totalCarriedForward),
      totalsCustomFields: formData.totalsCustomFields || {},
      additionalFields: formData.additionalFields || {},
      sector1: buildSectorPayload(overflowSectorNumber, formData)
    };

    try {
      let newEntryId = null;
      const tripId = formData?.openedForTripId?._id || formData?.openedForTripId;
      if (tripId && formData?._id) {
        const overflow = await axios.post(`/api/technical-log-book/${formData._id}/overflow-sheet`);
        newEntryId = overflow?.data?._id;
        if (!newEntryId) throw new Error('Failed to create next techlogbook');
        await axios.put(`/api/technical-log-book/${newEntryId}`, {
          ...headerPayload,
          sector1: {
            ...(overflow.data.sector1 || {}),
            ...headerPayload.sector1
          }
        });
      } else {
        const created = await axios.post('/api/technical-log-book/standalone', {
          aircraftId,
          entryDate: formData.entryDate || new Date()
        });
        newEntryId = created?.data?._id;
        if (!newEntryId) throw new Error('Failed to create next techlogbook');
        await axios.put(`/api/technical-log-book/${newEntryId}`, headerPayload);
      }
      showToast(`Redirected to next techlogbook sheet${nextSheetNumber ? ` ${nextSheetNumber}` : ''}`, 'success');
      if (typeof onOpenEntry === 'function') onOpenEntry(newEntryId);
    } catch (error) {
      console.error('Error creating overflow techlogbook:', error);
      showToast(error.response?.data?.message || 'Failed to create next techlogbook', 'error');
    }
  };

  const addSector = async (sectorNumber) => {
    if (!formData || !canEdit) return;
    if (sectorNumber < 2 || sectorNumber > MAX_TECHNICAL_LOG_BOOK_SECTOR_SLOTS) return;

    const maxSectorsPerTechlogBook = getMaxSectorsPerTechlogBook(formatDefinition);
    const highestSector = getHighestExistingSectorNumber(formData);
    if (highestSector >= maxSectorsPerTechlogBook && sectorNumber > maxSectorsPerTechlogBook) {
      await createAndOpenOverflowTechlogBook(sectorNumber);
      return;
    }

    const sectorKey = `sector${sectorNumber}`;
    const newSector = buildSectorPayload(sectorNumber, formData);
    if (!newSector) return;

    updateField(sectorKey, newSector);
    // Switch to the new sector tab
    setTabValue(1 + sectorNumber); // Header=0, Totals=1, Sector1=2, Sector2=3, etc.
    showToast(`Sector ${sectorNumber} added`, 'success');
  };

  // Remove the highest sector slot (sector2–sector5). Null is sent on save so the backend unsets it.
  // Only the highest slot may be removed to avoid gaps that break sector tab indexing.
  const removeSector = (sectorNumber) => {
    if (!formData || !canEdit) return;
    if (sectorNumber < 2 || sectorNumber > MAX_TECHNICAL_LOG_BOOK_SECTOR_SLOTS) return;
    if (!formData[`sector${sectorNumber}`]) return;

    const highest = getHighestExistingSectorNumber(formData);
    if (sectorNumber !== highest) {
      showToast(`Remove Sector ${highest} first (highest sector only).`, 'warning');
      return;
    }

    const confirmed = window.confirm(
      `Delete Sector ${sectorNumber} from this techlog sheet? Save Changes to persist.`
    );
    if (!confirmed) return;

    const sectorKey = `sector${sectorNumber}`;
    const removedLinkedId = formData[sectorKey]?.linkedSectorId;
    const newFormData = { ...formData };
    // Set to null so backend knows to delete it
    newFormData[sectorKey] = null;

    if (removedLinkedId) {
      const removedIdStr = String(removedLinkedId._id || removedLinkedId);
      if (Array.isArray(newFormData.sectorIds)) {
        newFormData.sectorIds = newFormData.sectorIds.filter(
          (id) => String(id?._id || id) !== removedIdStr
        );
      }
      const primaryId = newFormData.sectorId?._id || newFormData.sectorId;
      if (primaryId && String(primaryId) === removedIdStr) {
        newFormData.sectorId = newFormData.sectorIds?.[0] || null;
      }
    }

    setFormData(newFormData);

    // If we're on the removed sector tab, switch to Sector 1 (always present)
    if (tabValue === 1 + sectorNumber) {
      setTabValue(2);
    }
    showToast(`Sector ${sectorNumber} removed. Save to confirm.`, 'info');
  };

  const buildUpdatePayload = () => {
    // Deep copy — values are already in base units from onChange handlers
    const updatePayload = JSON.parse(JSON.stringify(formData));
    const safeFuel = (v) => {
      const n = Number(v ?? 0);
      return Number.isFinite(n) ? n : 0;
    };
    for (let i = 1; i <= 5; i += 1) {
      const sectorKey = `sector${i}`;
      const sector = updatePayload?.[sectorKey];
      if (!sector) continue; // skip missing or null (null means delete)
      sector.totalFuelBeforeFlight = safeFuel(sector.fuelInTankBF) + safeFuel(sector.fuelUplifted);
      sector.lhEngineOilBeforeFlight = safeFuel(sector.lhEngineOilInTankBF) + safeFuel(sector.lhEngineOilUplifted);
      sector.rhEngineOilBeforeFlight = safeFuel(sector.rhEngineOilInTankBF) + safeFuel(sector.rhEngineOilUplifted);
      sector.lhEngineOilConsumption = safeFuel(sector.lhEngineOilBeforeFlight) - safeFuel(sector.lhEngineOilAfterFlight);
      sector.rhEngineOilConsumption = safeFuel(sector.rhEngineOilBeforeFlight) - safeFuel(sector.rhEngineOilAfterFlight);
    }

    // Normalize Totals tab "hours" fields (HH:MM) back to numeric hours for backend schema
    if (formatDefinition && Array.isArray(formatDefinition.sections)) {
      const totalsSection = formatDefinition.sections.find((s) => s.id === 'totals');
      if (totalsSection && Array.isArray(totalsSection.fields)) {
        const convertHoursPath = (root, path) => {
          if (!root || !path) return;
          const keys = path.split('.');
          let obj = root;
          for (let i = 0; i < keys.length - 1; i += 1) {
            obj = obj?.[keys[i]];
            if (!obj || typeof obj !== 'object') return;
          }
          const lastKey = keys[keys.length - 1];
          const raw = obj[lastKey];
          if (raw === undefined || raw === null || raw === '') return;
          if (typeof raw === 'number') return;
          const numeric = parseHMMToHours(String(raw));
          obj[lastKey] = Number.isNaN(numeric) ? 0 : numeric;
        };

        totalsSection.fields.forEach((field) => {
          if (
            field &&
            field.type === 'hours' &&
            typeof field.path === 'string' &&
            (field.path.startsWith('totalBroughtForward.') || field.path.startsWith('totalCarriedForward.'))
          ) {
            convertHoursPath(updatePayload, field.path);
          }
        });
      }
    }

    const normalizeTotalsObject = (obj) => {
      if (!obj || typeof obj !== 'object') return;
      Object.keys(obj).forEach((key) => {
        const value = obj[key];
        if (value === '' || value === null || value === undefined) return;
        const num = Number(value);
        if (!Number.isFinite(num)) return;
        obj[key] = roundTotalsNumeric(key, num);
      });
    };
    normalizeTotalsObject(updatePayload.totalBroughtForward);
    normalizeTotalsObject(updatePayload.totalCarriedForward);
    if (updatePayload.engineChangeReferenceTlbId && typeof updatePayload.engineChangeReferenceTlbId === 'object') {
      updatePayload.engineChangeReferenceTlbId =
        updatePayload.engineChangeReferenceTlbId._id || updatePayload.engineChangeReferenceTlbId.id || null;
    }
    // Remove fields that shouldn't be updated
    delete updatePayload._id;
    delete updatePayload.__v;
    delete updatePayload.createdAt;
    delete updatePayload.updatedAt;
    delete updatePayload.fieldHistory;
    delete updatePayload.statusHistory;
    delete updatePayload.techlogProof;
    delete updatePayload._offlinePending;
    delete updatePayload._offlineUpdatedAt;

    return updatePayload;
  };

  const persistFormData = async ({ closeOnSuccess = true } = {}) => {
    if (!formData || !canEdit) return null;

    const updatePayload = buildUpdatePayload();
    const entryId = formData._id;
    const formSnapshot = {
      ...formData,
      ...updatePayload,
      _id: entryId,
      _offlinePending: true
    };

    const saveLocally = async (toastMessage, toastSeverity = 'info') => {
      await saveOfflineDraft({
        entryId,
        payload: updatePayload,
        formSnapshot
      });
      setFormData({
        ...formSnapshot,
        _offlinePending: true,
        _offlineUpdatedAt: Date.now()
      });
      setHasOfflinePending(true);
      await refreshPending();
      if (typeof onOfflineDraftChange === 'function') onOfflineDraftChange();
      showToast(toastMessage, toastSeverity);
      if (closeOnSuccess) onClose();
      return { ...formSnapshot, _offlinePending: true };
    };

    if (!isOnline || (typeof navigator !== 'undefined' && !navigator.onLine)) {
      return saveLocally('Saved offline — will sync when connected', 'info');
    }

    try {
      const response = await axios.put(`/api/technical-log-book/${entryId}`, updatePayload);
      try {
        await removeOfflineDraft(entryId);
      } catch (removeErr) {
        console.error('Failed to clear offline draft after save:', removeErr);
      }
      setFormData({ ...response.data, _offlinePending: false, _offlineUpdatedAt: null });
      setHasOfflinePending(false);
      await refreshPending();
      if (typeof onOfflineDraftChange === 'function') onOfflineDraftChange();

      if (closeOnSuccess) {
        showToast('Entry updated successfully', 'success');
        onClose();
      }

      return response.data;
    } catch (error) {
      if (isNetworkError(error)) {
        return saveLocally('Network unavailable — saved offline. Will sync when connected.', 'warning');
      }
      throw error;
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await persistFormData({ closeOnSuccess: true });
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to update entry', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncNow = async () => {
    if (!formData?._id || !isOnline) return;
    try {
      setSaving(true);
      const result = await syncOne(formData._id);
      if (result.ok) {
        showToast('Offline changes synced', 'success');
        await refreshPending();
        if (typeof onOfflineDraftChange === 'function') onOfflineDraftChange();
      } else {
        showToast(
          result.error?.response?.data?.message || result.error?.message || 'Failed to sync offline changes',
          'error'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData) return;

    if (!isOnline || (typeof navigator !== 'undefined' && !navigator.onLine)) {
      showToast('Connect to the internet to submit for review', 'warning');
      return;
    }

    try {
      setSaving(true);
      let entryId = formData._id;

      if (hasOfflinePending) {
        const syncResult = await syncOne(entryId);
        if (!syncResult.ok) {
          showToast(
            syncResult.error?.response?.data?.message ||
              syncResult.error?.message ||
              'Sync offline changes before submitting',
            'error'
          );
          return;
        }
        setHasOfflinePending(false);
      }

      if (canEdit) {
        const saved = await persistFormData({ closeOnSuccess: false });
        entryId = saved?._id || entryId;
        if (saved?._offlinePending) {
          showToast('Saved offline — connect to submit for review', 'warning');
          return;
        }
      }
      await axios.post(`/api/technical-log-book/${entryId}/submit`, { remarks });
      showToast('Entry submitted for review', 'success');
      onClose();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to submit entry', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleApproveClick = () => {
    setApproveDialogOpen(true);
  };

  const handleRejectClick = () => {
    setRejectDialogOpen(true);
  };

  const handleStartReviewClick = () => {
    setStartReviewDialogOpen(true);
  };

  const handleCorrectionClick = () => {
    setCorrectionDialogOpen(true);
  };

  const handleArchiveClick = () => {
    setArchiveDialogOpen(true);
  };

  const handleUnarchiveClick = () => {
    setUnarchiveDialogOpen(true);
  };

  const handleDeleteClick = () => {
    if (!formData?._id) {
      setDeleteDialogOpen(true);
      return;
    }
    setDeletePreviewLoading(true);
    axios.get(`/api/technical-log-book/${formData._id}/delete-preview`)
      .then((res) => {
        setDeleteDependencies(res.data?.dependencies || []);
      })
      .catch((err) => {
        console.error('Delete preview error:', err);
        showToast(err.response?.data?.message || 'Failed to load delete preview', 'error');
        setDeleteDependencies([]);
      })
      .finally(() => setDeletePreviewLoading(false));
    setDeleteDialogOpen(true);
  };

  const confirmApprove = () => {
    if (onApprove) {
      onApprove(formData._id, remarks);
    }
    setApproveDialogOpen(false);
    setRemarks('');
  };

  const confirmReject = () => {
    if (!remarks.trim()) {
      showToast('Rejection remarks are required', 'error');
      return;
    }
    if (onReject) {
      onReject(formData._id, remarks);
    }
    setRejectDialogOpen(false);
    setRemarks('');
  };

  const confirmStartReview = () => {
    if (onStartReview) {
      onStartReview(formData._id, remarks);
    }
    setStartReviewDialogOpen(false);
    setRemarks('');
  };

  const confirmCorrection = async () => {
    if (!remarks.trim()) {
      showToast('Correction remarks are required', 'error');
      return;
    }

    try {
      await axios.post(`/api/technical-log-book/${formData._id}/correct`, { remarks });
      showToast('Correction requested. Entry unlocked for editing.', 'success');
      setCorrectionDialogOpen(false);
      setRemarks('');
      onClose();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to request correction', 'error');
    }
  };

  const confirmArchive = async () => {
    if (!archiveReason.trim()) {
      showToast('Archive reason is required', 'error');
      return;
    }
    try {
      await axios.delete(`/api/technical-log-book/${formData._id}`, { data: { reason: archiveReason } });
      showToast('Entry archived successfully', 'success');
      setArchiveDialogOpen(false);
      setArchiveReason('');
      onClose();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to archive entry', 'error');
    }
  };

  const confirmUnarchive = async () => {
    try {
      const response = await axios.post(`/api/technical-log-book/${formData._id}/unarchive`, {
        reason: unarchiveReason.trim() || undefined
      });
      showToast('Entry restored from archive', 'success');
      setUnarchiveDialogOpen(false);
      setUnarchiveReason('');
      if (response.data) {
        setFormData({ ...response.data, _offlinePending: false, _offlineUpdatedAt: null });
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to unarchive entry', 'error');
    }
  };

  const confirmDelete = async () => {
    if (!deleteReason.trim()) {
      showToast('Delete reason is required', 'error');
      return;
    }
    if (deleteConfirmText.trim() !== 'DELETE') {
      showToast('Please type DELETE to confirm', 'error');
      return;
    }
    try {
      await axios.delete(`/api/technical-log-book/${formData._id}`, {
        data: { reason: deleteReason, confirm: true }
      });
      showToast('Entry permanently deleted', 'success');
      setDeleteDialogOpen(false);
      setDeleteReason('');
      setDeleteConfirmText('');
      setDeleteDependencies([]);
      onClose();
    } catch (error) {
      const data = error.response?.data;
      if (data?.requiresConfirmation) {
        showToast(data.message || 'Delete requires confirmation', 'error');
        return;
      }
      showToast(data?.message || 'Failed to delete entry', 'error');
    }
  };

  const refreshEntryFromServer = async () => {
    if (!formData?._id) return;
    try {
      const res = await axios.get(`/api/technical-log-book/${formData._id}`);
      setFormData(res.data);
    } catch (error) {
      console.error('Error refreshing technical log book:', error);
      showToast(error.response?.data?.message || 'Failed to refresh entry', 'error');
    }
  };

  const updateField = (path, value) => {
    if (!formData) return;
    
    const keys = path.split('.');
    const newData = { ...formData };
    let current = newData;
    
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {};
      } else {
        current[keys[i]] = { ...current[keys[i]] };
      }
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = value;
    setFormData(newData);
  };

  const updateNestedField = (parentPath, field, value) => {
    updateField(`${parentPath}.${field}`, value);
  };

  const getPreviousSheetEntry = async ({ requireTotals = false } = {}) => {
    if (!formData) return null;
    const currentSheetNumber = parseSheetNumber(formData.sheetNumber);
    if (!currentSheetNumber || currentSheetNumber <= 1) return null;

    const aircraftId = formData.aircraftId?._id || formData.aircraftId;
    if (!aircraftId) return null;

    const response = await axios.get(`/api/technical-log-book/aircraft/${aircraftId}`);
    const entries = Array.isArray(response.data) ? response.data : [];
    const previousSheetNumber = currentSheetNumber - 1;
    const previousEntry = entries.find((item) => {
      const itemSheetNumber = parseSheetNumber(item?.sheetNumber);
      return (
        item &&
        String(item._id) !== String(formData._id) &&
        itemSheetNumber === previousSheetNumber &&
        (!requireTotals || (item.totalCarriedForward && typeof item.totalCarriedForward === 'object'))
      );
    });

    if (!previousEntry) return null;
    return { previousEntry, previousSheetNumber };
  };
  const getPreviousSheetEntryWithTotals = async () => getPreviousSheetEntry({ requireTotals: true });

  const getBroughtForwardSourceEntry = async () => {
    const previous = await getPreviousSheetEntryWithTotals();
    if (!previous?.previousEntry) return null;
    return {
      sourceEntry: previous.previousEntry,
      sourceSheetNumber: previous.previousSheetNumber
    };
  };

  const handleCalculateTotalBroughtForward = async () => {
    if (!formData) return;
    try {
      const source = await getBroughtForwardSourceEntry();
      if (!source) {
        showToast('Reference Techlog with Total Carried Forward not found.', 'warning');
        return;
      }

      const roundedBroughtForward = Object.entries(
        sanitizeBroughtForwardTotals(source.sourceEntry.totalCarriedForward)
      ).reduce((acc, [key, value]) => {
        const n = Number(value);
        acc[key] = Number.isFinite(n) ? roundTotalsNumeric(key, n) : 0;
        return acc;
      }, {});
      updateField('totalBroughtForward', roundedBroughtForward);
      showToast(`Total Brought Forward updated from sheet ${source.sourceSheetNumber}.`, 'success');
    } catch (error) {
      console.error('Error calculating Total Brought Forward:', error);
      showToast('Failed to calculate Total Brought Forward', 'error');
    }
  };

  const handleCalculateTotalCarriedForward = () => {
    if (!formData) return;
    const totalsSection = getTotalsSection(formatDefinition?.sections?.find((s) => s.id === 'totals'));
    const totalsFieldKeys = (totalsSection?.fields || [])
      .map((field) => field.path?.split('.').pop())
      .filter(Boolean);
    const customFieldKeys = (totalsSection?.customFields || []).map((f) => f.key).filter(Boolean);
    const baseKeys = [...new Set([
      ...totalsFieldKeys,
      ...customFieldKeys.map((k) => (k.includes('.') ? k.split('.').pop() : k))
    ])];
    const carried = computeTotalCarriedForward(formData, baseKeys);
    updateField('totalCarriedForward', carried);
    if (customFieldKeys.length > 0) {
      const existing = formData.totalsCustomFields || {};
      const newTotalsCustom = { ...existing };
      for (const formatKey of customFieldKeys) {
        if (formatKey.startsWith('totalBroughtForward.')) {
          newTotalsCustom[formatKey] = 0;
        } else if (formatKey.startsWith('totalCarriedForward.')) {
          const baseKey = formatKey.includes('.') ? formatKey.split('.').pop() : formatKey;
          const value = carried[baseKey] !== undefined && carried[baseKey] !== null ? carried[baseKey] : (carried[formatKey] !== undefined && carried[formatKey] !== null ? carried[formatKey] : 0);
          newTotalsCustom[formatKey] = value;
        }
      }
      updateField('totalsCustomFields', newTotalsCustom);
    }
    showToast('Total Carried Forward calculated from Brought Forward + all sectors.', 'success');
  };

  const setSectorNestedField = (sector, fieldPath, value) => {
    const keys = fieldPath.split('.');
    let current = sector;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {};
      } else if (typeof current[keys[i]] === 'object' && !Array.isArray(current[keys[i]])) {
        current[keys[i]] = { ...current[keys[i]] };
      }
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
  };

  const applySectorDerivedFields = (sector, fieldPath) => {
    if (fieldPath === 'rotorStartTimeUTC' || fieldPath === 'rotorStopTimeUTC') {
      sector.blockTime = calculateBlockTimeHHMM(sector.rotorStartTimeUTC, sector.rotorStopTimeUTC);
    }
  };

  const updateSectorField = (sectorKey, fieldPath, value) => {
    if (isAmeSignatureValuePath(fieldPath) || isAmeSignatureTypePath(fieldPath)) {
      return;
    }
    setFormData((prev) => {
      if (!prev || !prev[sectorKey]) return prev;
      const sector = { ...prev[sectorKey] };
      setSectorNestedField(sector, fieldPath, value);
      applySectorDerivedFields(sector, fieldPath);
    if (fieldPath === 'fuelInTankBF' || fieldPath === 'fuelUplifted') {
      const fuelInTankBF = Number(sector.fuelInTankBF ?? 0);
      const fuelUplifted = Number(sector.fuelUplifted ?? 0);
      sector.totalFuelBeforeFlight =
        (Number.isFinite(fuelInTankBF) ? fuelInTankBF : 0) +
        (Number.isFinite(fuelUplifted) ? fuelUplifted : 0);
    }
    if (
      fieldPath === 'lhEngineOilInTankBF' ||
      fieldPath === 'lhEngineOilUplifted'
    ) {
      const lhEngineOilInTankBF = Number(sector.lhEngineOilInTankBF ?? 0);
      const lhEngineOilUplifted = Number(sector.lhEngineOilUplifted ?? 0);
      sector.lhEngineOilBeforeFlight =
        (Number.isFinite(lhEngineOilInTankBF) ? lhEngineOilInTankBF : 0) +
        (Number.isFinite(lhEngineOilUplifted) ? lhEngineOilUplifted : 0);
    }
    if (
      fieldPath === 'rhEngineOilInTankBF' ||
      fieldPath === 'rhEngineOilUplifted'
    ) {
      const rhEngineOilInTankBF = Number(sector.rhEngineOilInTankBF ?? 0);
      const rhEngineOilUplifted = Number(sector.rhEngineOilUplifted ?? 0);
      sector.rhEngineOilBeforeFlight =
        (Number.isFinite(rhEngineOilInTankBF) ? rhEngineOilInTankBF : 0) +
        (Number.isFinite(rhEngineOilUplifted) ? rhEngineOilUplifted : 0);
    }
    if (
      fieldPath === 'lhEngineOilInTankBF' ||
      fieldPath === 'lhEngineOilUplifted' ||
      fieldPath === 'lhEngineOilBeforeFlight' ||
      fieldPath === 'lhEngineOilAfterFlight'
    ) {
      const lhEngineOilBeforeFlight = Number(sector.lhEngineOilBeforeFlight ?? 0);
      const lhEngineOilAfterFlight = Number(sector.lhEngineOilAfterFlight ?? 0);
      sector.lhEngineOilConsumption =
        (Number.isFinite(lhEngineOilBeforeFlight) ? lhEngineOilBeforeFlight : 0) -
        (Number.isFinite(lhEngineOilAfterFlight) ? lhEngineOilAfterFlight : 0);
    }
    if (
      fieldPath === 'rhEngineOilInTankBF' ||
      fieldPath === 'rhEngineOilUplifted' ||
      fieldPath === 'rhEngineOilBeforeFlight' ||
      fieldPath === 'rhEngineOilAfterFlight'
    ) {
      const rhEngineOilBeforeFlight = Number(sector.rhEngineOilBeforeFlight ?? 0);
      const rhEngineOilAfterFlight = Number(sector.rhEngineOilAfterFlight ?? 0);
      sector.rhEngineOilConsumption =
        (Number.isFinite(rhEngineOilBeforeFlight) ? rhEngineOilBeforeFlight : 0) -
        (Number.isFinite(rhEngineOilAfterFlight) ? rhEngineOilAfterFlight : 0);
    }
    if (
      fieldPath === 'fuelInTankBF' ||
      fieldPath === 'fuelUplifted' ||
      fieldPath === 'totalFuelBeforeFlight' ||
      fieldPath === 'fuelAfterFlight'
    ) {
      const totalFuelBeforeFlight = Number(sector.totalFuelBeforeFlight ?? 0);
      const fuelAfterFlight = Number(sector.fuelAfterFlight ?? 0);
      sector.fuelConsumption =
        (Number.isFinite(totalFuelBeforeFlight) ? totalFuelBeforeFlight : 0) -
        (Number.isFinite(fuelAfterFlight) ? fuelAfterFlight : 0);
    }
    return { ...prev, [sectorKey]: sector };
  });
  };

  const updateAmeSignatureField = (sectorKey, valuePath, typePath, value, signatureType) => {
    setFormData((prev) => {
      if (!prev || !prev[sectorKey]) return prev;
      let sector = { ...prev[sectorKey] };
      setSectorNestedField(sector, valuePath, value);
      setSectorNestedField(sector, typePath, signatureType || 'NAME');
      sector = applyAmeSignatureAutofillToSector(sector, valuePath, value, signatureType || 'NAME');
      return { ...prev, [sectorKey]: sector };
    });
  };

  const updateInspectionAmeSignatureField = (sectorKey, inspectionIndex, value, signatureType) => {
    const valuePath = `inspections.${inspectionIndex}.engineCleaningDetails.ameAuthPersonSignature`;
    setFormData((prev) => {
      if (!prev || !prev[sectorKey]) return prev;
      let sector = { ...prev[sectorKey] };
      const inspections = [...(sector.inspections || [])];
      const item = {
        ...(inspections[inspectionIndex] || {}),
        engineCleaningDetails: {
          ...(inspections[inspectionIndex]?.engineCleaningDetails || {}),
          ameAuthPersonSignature: value,
          ameAuthPersonSignatureType: signatureType || 'NAME'
        }
      };
      inspections[inspectionIndex] = item;
      sector = { ...sector, inspections };
      sector = applyAmeSignatureAutofillToSector(sector, valuePath, value, signatureType || 'NAME');
      return { ...prev, [sectorKey]: sector };
    });
  };

useEffect(() => {
    const autoFillSector1FuelInTankBF = async () => {
      if (!entry || !formData) return;
      const sector1 = formData.sector1;
      if (!sector1 || typeof sector1 !== 'object') return;
      if (!isAutoFillEligibleSector1FuelInTankBF(sector1)) return;

      const previous = await getPreviousSheetEntry();
      if (!previous?.previousEntry) return;
      const prevFuelAfter = getLastSectorFuelAfterFlight(previous.previousEntry);
      if (prevFuelAfter === null) return;

      updateSectorField('sector1', 'fuelInTankBF', prevFuelAfter);
    };

    autoFillSector1FuelInTankBF().catch((error) => {
      console.error('Error auto-filling Sector 1 fuel in tank BF:', error);
    });
}, [
    entry,
    formData?._id,
    formData?.sheetNumber,
    formData?.sector1?.fuelInTankBF,
    formData?.sector1?.fuelUplifted,
    formData?.sector1?.fuelAfterFlight
  ]);

  const updateSectorArrayField = (sectorKey, arrayName, index, fieldPath, value) => {
    const inspectionAmeValuePath = `inspections.${index}.engineCleaningDetails.ameAuthPersonSignature`;
    const inspectionAmeTypePath = `inspections.${index}.engineCleaningDetails.ameAuthPersonSignatureType`;
    if (
      arrayName === 'inspections' &&
      (fieldPath === 'engineCleaningDetails.ameAuthPersonSignature' ||
        fieldPath === inspectionAmeValuePath ||
        fieldPath === 'engineCleaningDetails.ameAuthPersonSignatureType' ||
        fieldPath === inspectionAmeTypePath)
    ) {
      return;
    }
    setFormData((prev) => {
      if (!prev || !prev[sectorKey]) return prev;
      const sector = { ...prev[sectorKey] };
    const array = [...(sector[arrayName] || [])];
    const item = { ...array[index] };
    const keys = fieldPath.split('.');
    let current = item;
    
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {};
      } else if (typeof current[keys[i]] === 'object' && !Array.isArray(current[keys[i]])) {
        current[keys[i]] = { ...current[keys[i]] };
      }
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = value;
    array[index] = item;
    sector[arrayName] = array;
    return { ...prev, [sectorKey]: sector };
  });
  };

  const addInspection = (sectorKey) => {
    if (!formData || !formData[sectorKey]) return;
    const sector = { ...formData[sectorKey] };
    const inspections = [...(sector.inspections || [])];
    inspections.push({
      typeOfInspection: '',
      taskCardIssued: '',
      taskCardReviewed: '',
      authorizedPersonPilotSignature: '',
      authorizedPersonPilotSignatureType: 'NAME',
      authNumberCHPLNumber: '',
      inspectionTime: '',
      inspectionDate: null,
      oilUpliftmentRecords: {},
      engineCleaningDetails: {}
    });
    sector.inspections = inspections;
    updateField(sectorKey, sector);
  };

  const removeInspection = (sectorKey, index) => {
    if (!formData || !formData[sectorKey]) return;
    const sector = { ...formData[sectorKey] };
    const inspections = [...(sector.inspections || [])];
    inspections.splice(index, 1);
    sector.inspections = inspections;
    updateField(sectorKey, sector);
  };

  const getStatusColor = (status) => {
    const colors = {
      'DRAFT': 'default',
      'SUBMITTED': 'warning',
      'UNDER_REVIEW': 'info',
      'APPROVED': 'success',
      'REJECTED': 'error'
    };
    return colors[status] || 'default';
  };

  if (!entry || !formData) {
    return null;
  }

  const isReadOnly = !canEdit || formData.workflowStatus === 'APPROVED';
  const canSubmit = formData.workflowStatus === 'DRAFT' && 
    [ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER, ROLES.SUPER_ADMIN].includes(user?.role) &&
    isOnline;
  const canStartReview = formData.workflowStatus === 'SUBMITTED' && canApprove;
  const canApproveEntry = (formData.workflowStatus === 'SUBMITTED' || formData.workflowStatus === 'UNDER_REVIEW') && canApprove;
  const canCorrect = formData.workflowStatus === 'APPROVED' && [
    ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER,
    ROLES.CA_MANAGER,
    ROLES.ACCOUNTABLE_MANAGER,
    ROLES.SUPER_ADMIN
  ].includes(user?.role);
  const canArchiveOrDelete = user?.role === ROLES.SUPER_ADMIN;
  const canManageSectors =
    canEdit && [ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER, ROLES.CA_MANAGER, ROLES.SUPER_ADMIN].includes(user?.role);
  const isArchived = formData.isActive === false;

  const headerSection = formatDefinition?.sections?.find(s => s.id === 'header');
  const totalsSection = getTotalsSection(formatDefinition?.sections?.find(s => s.id === 'totals'));
  const sectorSection = formatDefinition?.sections?.find(s => s.id === 'sector');
  const maxSectorsPerTechlogBook = getMaxSectorsPerTechlogBook(formatDefinition);
  const additionalSection = formatDefinition?.sections?.find(s => s.id === 'additional');
  const showAdditionalTab = additionalSection && (additionalSection.customFields?.length > 0) && (additionalSection.visible !== false);
  const additionalTabIndex = 2 + getActiveSectors().length; // After Header(0), Totals(1), Sector1..SectorN
  const isSectorTab = tabValue >= 2 && (!showAdditionalTab || tabValue < additionalTabIndex);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xl" 
      fullWidth
      PaperProps={{ sx: { height: '90vh' } }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">Technical Log Book Entry</Typography>
            <Typography variant="body2" color="text.secondary">
              Sheet: {formData.sheetNumber || 'Not set'} | Aircraft: {formData.aircraftId?.registration || 'N/A'}
            </Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            {!isOnline && (
              <Chip
                icon={<CloudOff />}
                label="Offline"
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
            {hasOfflinePending && (
              <Chip
                icon={<CloudUpload />}
                label="Pending sync"
                size="small"
                color="info"
                variant="outlined"
              />
            )}
            <Chip 
              label={formData.workflowStatus} 
              color={getStatusColor(formData.workflowStatus)}
              size="small"
            />
            <IconButton size="small" onClick={() => setShowHistory(!showHistory)}>
              <History />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <FieldHistorySection fieldHistory={fieldHistory} showHistory={showHistory} />

        {/* Workflow Status Display */}
        <Box mb={2}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Workflow Status</Typography>
            <Chip 
              label={formData.workflowStatus} 
              color={getStatusColor(formData.workflowStatus)}
              sx={{ mb: 1 }}
            />
            {formData.statusHistory && formData.statusHistory.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Last updated: {displayTimezone ? formatDateTimeWithTimezone(formData.statusHistory[formData.statusHistory.length - 1].changedAt, null, displayTimezone) : dayjs(formData.statusHistory[formData.statusHistory.length - 1].changedAt).format('DD MMM YYYY, HH:mm')}
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>

        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
          <Tab label={headerSection?.label || 'Header'} />
          <Tab label={totalsSection?.label || 'Totals'} />
          <Tab label="Sector 1" />
          {formData?.sector2 && <Tab label="Sector 2" />}
          {formData?.sector3 && <Tab label="Sector 3" />}
          {formData?.sector4 && <Tab label="Sector 4" />}
          {formData?.sector5 && <Tab label="Sector 5" />}
          {showAdditionalTab && <Tab label={additionalSection?.label || 'Additional'} />}
        </Tabs>

        {/* Header Tab */}
        {tabValue === 0 && (
          <HeaderTab
            formData={formData}
            isReadOnly={isReadOnly}
            updateField={updateField}
            canEdit={canEdit}
            headerSection={headerSection}
            displayTimezone={displayTimezone}
            onRefreshEntry={refreshEntryFromServer}
          />
        )}

        {/* Totals Tab */}
        {tabValue === 1 && (
          <TotalsTab
            formData={formData}
            isReadOnly={isReadOnly}
            updateNestedField={updateNestedField}
            updateField={updateField}
            totalsSection={totalsSection}
            onCalculateTotalBroughtForward={canEdit ? handleCalculateTotalBroughtForward : null}
            onCalculateTotalCarriedForward={canEdit ? handleCalculateTotalCarriedForward : null}
            displayTimezone={displayTimezone}
          />
        )}

        {/* Sector Tabs */}
        {isSectorTab && (
          <>
            {renderSectorForm({
              sectorNumber: tabValue - 1,
              formData,
              tabValue,
              natureOfFlightMapping,
              unitPreferences,
              isReadOnly,
              updateSectorField,
              updateAmeSignatureField,
              updateInspectionAmeSignatureField,
              updateSectorArrayField,
              addInspection,
              removeInspection,
              entityId: formData._id,
              entityType: 'TECHNICAL_LOG_BOOK',
              sectorSection,
              displayTimezone,
              availableSectors,
              allCrewOptions,
              canManageSectors,
              maxSectorsPerTechlogBook,
              onAddSector: addSector,
              onRemoveSector: removeSector,
              onAddNatureOfFlightMapping: canEdit ? handleAddNatureOfFlightMapping : null
            })}
          </>
        )}

        {/* Additional (custom fields) Tab */}
        {showAdditionalTab && tabValue === additionalTabIndex && (
          <AdditionalFieldsTab
            formData={formData}
            isReadOnly={isReadOnly}
            updateField={updateField}
            additionalSection={additionalSection}
            displayTimezone={displayTimezone}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {canEdit && hasOfflinePending && isOnline && (
          <Button
            variant="outlined"
            startIcon={offlineSyncing || saving ? <CircularProgress size={20} /> : <Sync />}
            onClick={handleSyncNow}
            disabled={saving || offlineSyncing}
          >
            Sync now
          </Button>
        )}
        {canEdit && (
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
            onClick={handleSave}
            disabled={saving}
          >
            {isOnline ? 'Save Changes' : 'Save Offline'}
          </Button>
        )}
        {canSubmit && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={saving || offlineSyncing}
          >
            Submit for Review
          </Button>
        )}
        {canStartReview && (
          <Button
            variant="outlined"
            color="info"
            onClick={handleStartReviewClick}
          >
            Start Review
          </Button>
        )}
        {canApproveEntry && (
          <>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Cancel />}
              onClick={handleRejectClick}
            >
              Reject
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              onClick={handleApproveClick}
            >
              Approve
            </Button>
          </>
        )}
        {canCorrect && (
          <Button
            variant="outlined"
            color="warning"
            onClick={handleCorrectionClick}
          >
            Request Correction
          </Button>
        )}
        {canArchiveOrDelete && !isArchived && (
          <Button variant="outlined" color="warning" onClick={handleArchiveClick}>
            Archive
          </Button>
        )}
        {canArchiveOrDelete && isArchived && (
          <>
            <Button variant="outlined" color="success" onClick={handleUnarchiveClick}>
              Unarchive
            </Button>
            <Button variant="outlined" color="error" onClick={handleDeleteClick}>
              Permanently Delete
            </Button>
          </>
        )}
      </DialogActions>

      <ActionDialogs
        approveDialogOpen={approveDialogOpen}
        rejectDialogOpen={rejectDialogOpen}
        startReviewDialogOpen={startReviewDialogOpen}
        correctionDialogOpen={correctionDialogOpen}
        archiveDialogOpen={archiveDialogOpen}
        unarchiveDialogOpen={unarchiveDialogOpen}
        deleteDialogOpen={deleteDialogOpen}
        remarks={remarks}
        setRemarks={setRemarks}
        archiveReason={archiveReason}
        setArchiveReason={setArchiveReason}
        unarchiveReason={unarchiveReason}
        setUnarchiveReason={setUnarchiveReason}
        deleteReason={deleteReason}
        setDeleteReason={setDeleteReason}
        deleteConfirmText={deleteConfirmText}
        setDeleteConfirmText={setDeleteConfirmText}
        deleteDependencies={deleteDependencies}
        deletePreviewLoading={deletePreviewLoading}
        onCloseApprove={() => setApproveDialogOpen(false)}
        onCloseReject={() => setRejectDialogOpen(false)}
        onCloseStartReview={() => setStartReviewDialogOpen(false)}
        onCloseCorrection={() => setCorrectionDialogOpen(false)}
        onCloseArchive={() => {
          setArchiveDialogOpen(false);
          setArchiveReason('');
        }}
        onCloseUnarchive={() => {
          setUnarchiveDialogOpen(false);
          setUnarchiveReason('');
        }}
        onCloseDelete={() => {
          setDeleteDialogOpen(false);
          setDeleteReason('');
          setDeleteConfirmText('');
          setDeleteDependencies([]);
        }}
        onConfirmApprove={confirmApprove}
        onConfirmReject={confirmReject}
        onConfirmStartReview={confirmStartReview}
        onConfirmCorrection={confirmCorrection}
        onConfirmArchive={confirmArchive}
        onConfirmUnarchive={confirmUnarchive}
        onConfirmDelete={confirmDelete}
      />
    </Dialog>
  );
};

export default TechnicalLogBookEntry;
